import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const AuthModal = ({ isOpen, onClose, initialMode = 'signin', onSignupSuccess, onLoginSuccess }) => {
  const navigate = useNavigate();
  const { login, signup, loginWithGoogle } = useAuth();

  console.log('initialMode is', initialMode);

  const [mode, setMode] = useState(initialMode); // 'signin', 'signup', 'signup_success', 'forgot_password'
  const [forgotPasswordStep, setForgotPasswordStep] = useState('request'); // 'request' or 'reset'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    otp: '',
    newPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [signupSuccessData, setSignupSuccessData] = useState(null);

  // Add debugging
  console.log('🔵 AuthModal rendered with isOpen:', isOpen, 'mode:', mode, 'forgotPasswordStep:', forgotPasswordStep);

  useEffect(() => {
    if (isOpen) {
      console.log('🔵 useEffect: Setting mode to', initialMode);
      setMode(initialMode);
      setForgotPasswordStep('request');
      setFormData({ email: '', password: '', name: '', otp: '', newPassword: '' });
      setErrors({});
    }
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('🔵 Input change:', name, '=', value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    console.log('🔵 Validating form with data:', formData);

    if (mode === 'signin' || mode === 'signup' || mode === 'forgot_password') {
      if (!formData.email) {
        console.log('❌ Email validation failed: empty');
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        console.log('❌ Email validation failed: invalid format');
        newErrors.email = 'Email is invalid';
      } else {
        console.log('✅ Email validation passed');
      }
    }

    if (mode === 'signin' || mode === 'signup') {
      if (!formData.password) {
        console.log('❌ Password validation failed: empty');
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        console.log('❌ Password validation failed: too short');
        newErrors.password = 'Password must be at least 6 characters';
      } else {
        console.log('✅ Password validation passed');
      }
    }

    if (mode === 'signup' && !formData.name) {
      console.log('❌ Name validation failed: empty');
      newErrors.name = 'Name is required';
    } else if (mode === 'signup') {
      console.log('✅ Name validation passed');
    }

    if (mode === 'forgot_password' && forgotPasswordStep === 'reset') {
      if (!formData.otp) {
        console.log('❌ OTP validation failed: empty');
        newErrors.otp = 'OTP is required';
      } else if (!/^\d{6}$/.test(formData.otp)) {
        console.log('❌ OTP validation failed: invalid format');
        newErrors.otp = 'OTP must be a 6-digit number';
      } else {
        console.log('✅ OTP validation passed');
      }

      if (!formData.newPassword) {
        console.log('❌ New password validation failed: empty');
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 6) {
        console.log('❌ New password validation failed: too short');
        newErrors.newPassword = 'New password must be at least 6 characters';
      } else {
        console.log('✅ New password validation passed');
      }
    }

    console.log('🔵 Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔵 Form submission started');
    console.log('🔵 Form data:', formData);
    console.log('🔵 Mode:', mode);
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    setLoading(true);
    
    try {
      if (mode === 'signin') {
        console.log('🔵 Attempting login...');
        const result = await login(formData.email, formData.password);
        console.log('🔵 Auth result:', result);

        if (result.success) {
          console.log('✅ Login successful, closing modal');
          onClose();
          setFormData({ email: '', password: '', name: '', otp: '', newPassword: '' });
          setErrors({});
          if (!result.subscription || (result.subscription && result.subscription?.package_config?.price == 0)) {
            navigate('/pricing');
          }
        } else {
          console.log('❌ Authentication failed:', result.error);
          setErrors({ general: result.error });
        }
      } else if (mode === 'signup') {
        console.log('🔵 Attempting signup...');
        const result = await signup(formData.email, formData.password, formData.name);
        console.log('🔵 Auth result:', result);

        if (result.success) {
          console.log('✅ Signup successful, prompting user to login');
          setSignupSuccessData(result);
          setMode('signup_success');
          setFormData({ email: formData.email, password: '', name: '', otp: '', newPassword: '' });
          setErrors({});
        } else {
          console.log('❌ Authentication failed:', result.error);
          setErrors({ general: result.error });
        }
      } else if (mode === 'forgot_password' && forgotPasswordStep === 'request') {
        console.log('🔵 Attempting OTP request...');
        const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email: formData.email }),
        });
        const result = await response.json();
        console.log('🔵 OTP request result:', result);

        if (response.ok && result.success) {
          console.log('✅ OTP sent successfully');
          setForgotPasswordStep('reset');
          setErrors({});
        } else {
          console.log('❌ OTP request failed:', result.detail || result.message);
          setErrors({ general: result.detail || result.message || 'Failed to send OTP' });
        }
      } else if (mode === 'forgot_password' && forgotPasswordStep === 'reset') {
        console.log('🔵 Attempting password reset...');
        const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/auth/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            email: formData.email,
            otp: formData.otp,
            new_password: formData.newPassword,
          }),
        });
        const result = await response.json();
        console.log('🔵 Password reset result:', result);

        if (response.ok && result.success) {
          console.log('✅ Password reset successful');
          setMode('signin');
          setFormData({ email: formData.email, password: '', name: '', otp: '', newPassword: '' });
          setErrors({});
          setForgotPasswordStep('request');
        } else {
          console.log('❌ Password reset failed:', result.detail || result.message);
          setErrors({ general: result.detail || result.message || 'Failed to reset password' });
        }
      }
    } catch (error) {
      console.error('🔴 Authentication error:', error);
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const switchMode = () => {
    if (mode === 'signup_success') {
      setMode('signin');
    } else if (mode === 'forgot_password') {
      setMode('signin');
      setForgotPasswordStep('request');
    } else {
      setMode(mode === 'signin' ? 'signup' : 'signin');
    }
    setFormData({ email: '', password: '', name: '', otp: '', newPassword: '' });
    setErrors({});
  };

  const handleForgotPassword = () => {
    setMode('forgot_password');
    setForgotPasswordStep('request');
    setFormData({ email: formData.email, password: '', name: '', otp: '', newPassword: '' });
    setErrors({});
  };

  const handleProceedToLogin = () => {
    setMode('signin');
    setErrors({});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      {/* Background gradient effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse delay-1000"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-[#54edb6]/20 rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-[#54edb6]/20">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {mode === 'signin' ? 'Welcome back' : 
                 mode === 'signup_success' ? 'Account created!' : 
                 mode === 'forgot_password' ? 
                   (forgotPasswordStep === 'request' ? 'Forgot Password' : 'Reset Password') : 
                   'Create your account'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-[#54edb6] text-2xl transition-colors"
              >
                ×
              </button>
            </div>
            <p className="text-gray-400 text-sm mt-1">
              {mode === 'signin' ? 'Please sign in to access your immigration assistance' : 
               mode === 'signup_success' ? 'Your account has been created successfully! Please log in to continue.' :
               mode === 'forgot_password' ? 
                 (forgotPasswordStep === 'request' ? 
                   'Enter your email to receive a password reset OTP' : 
                   'Enter the OTP sent to your email and your new password') :
                 'Welcome! Please fill in the details to get started'}
            </p>
          </div>

          <div className="p-6">
            {mode === 'signup_success' ? (
              /* Signup Success Content */
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-white">
                    Welcome to immigrantvli!
                  </h3>
                  <p className="text-gray-300">
                    Your account has been created successfully.<br />
                    Please log in to choose your plan and get started.
                  </p>
                </div>
                <button
                  onClick={handleProceedToLogin}
                  className="w-full bg-[#54edb6] hover:bg-[#54edb6]/90 text-black font-semibold py-3 px-4 rounded-lg transition-all duration-200 hover:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                >
                  Continue to Login
                </button>
              </div>
            ) : (
              /* Regular Login/Signup/Forgot Password Content */
              <>
                {mode !== 'forgot_password' && (
                  <button
                    onClick={handleGoogleLogin}
                    className="w-full mb-4 bg-white hover:bg-gray-100 text-gray-800 py-3 px-4 rounded-lg flex items-center justify-center transition-all duration-200 border border-gray-300 hover:shadow-[0_0_10px_rgba(255,255,255,0.3)]"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.20-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                )}

                {mode !== 'forgot_password' && (
                  <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-900 text-gray-400">or</span>
                    </div>
                  </div>
                )}

                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-5"
                  onClick={() => console.log('🔵 Form clicked')}
                >
                  <div style={{ display: 'none' }}>
                    {console.log('🔵 Form element rendered')}
                  </div>

                  {mode === 'signup' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6]/30 transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6]/30 transition-all duration-200"
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>

                  {(mode === 'signin' || mode === 'signup') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6]/30 transition-all duration-200"
                        placeholder="Enter your password"
                      />
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                      )}
                    </div>
                  )}

                  {mode === 'forgot_password' && forgotPasswordStep === 'reset' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          OTP
                        </label>
                        <input
                          type="text"
                          name="otp"
                          value={formData.otp}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6]/30 transition-all duration-200"
                          placeholder="Enter the 6-digit OTP"
                        />
                        {errors.otp && (
                          <p className="mt-1 text-sm text-red-400">{errors.otp}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6]/30 transition-all duration-200"
                          placeholder="Enter your new password"
                        />
                        {errors.newPassword && (
                          <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>
                        )}
                      </div>
                    </>
                  )}

                  {errors.general && (
                    <div className="p-3 bg-red-900/30 border border-red-400 rounded-lg">
                      <p className="text-sm text-red-400">{errors.general}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-6 py-3 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200 shadow-lg hover:shadow-[0_0_15px_rgba(84,237,182,0.4)] disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                        {mode === 'signin' ? 'Signing in...' : 
                         mode === 'signup' ? 'Creating account...' : 
                         forgotPasswordStep === 'request' ? 'Sending OTP...' : 'Resetting Password...'}
                      </div>
                    ) : (
                      mode === 'signin' ? 'Sign in' : 
                      mode === 'signup' ? 'Create account' : 
                      forgotPasswordStep === 'request' ? 'Send OTP' : 'Reset Password'
                    )}
                  </button>
                </form>

                {mode === 'signin' && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={handleForgotPassword}
                      className="text-[#54edb6] hover:text-[#54edb6]/80 text-sm font-medium transition-colors"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                {mode !== 'forgot_password' && (
                  <div className="mt-6 text-center">
                    <span className="text-gray-400 text-sm">
                      {mode === 'signin' ? "Don't have an account? " : "Already have an account? "}
                    </span>
                    <button
                      onClick={switchMode}
                      className="text-[#54edb6] hover:text-[#54edb6]/80 text-sm font-medium transition-colors"
                    >
                      {mode === 'signin' ? 'Sign up' : 'Sign in'}
                    </button>
                  </div>
                )}

                {mode === 'forgot_password' && (
                  <div className="mt-6 text-center">
                    <span className="text-gray-400 text-sm">
                      Back to{' '}
                    </span>
                    <button
                      onClick={switchMode}
                      className="text-[#54edb6] hover:text-[#54edb6]/80 text-sm font-medium transition-colors"
                    >
                      Sign in
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;